import React, { useCallback, useMemo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import Alert from '@r1-frontend/ui-react/components/Alert';
import Button from '@r1-frontend/ui-react/components/buttons/button';
import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { InfoCircle, TickCircle } from '@r1-frontend/ui-react/components/svg/main';
import { H3 } from '@r1-frontend/ui-react/components/typography/heading';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import { BlockContainer, FullWidthListContainer } from '@r1-frontend/ui-react/experimental/containers';
import { useMatchMediaByWidth } from '@r1-frontend/shared/hooks/useMatchMedia';

import { USER_LOGIN_ROUTE } from '~/src/constants/routes';
import CallbackContacts from '~/src/features/tariffBuilder/components/CallbackContacts';
import SummaryInfo from '~/src/features/tariffBuilder/components/SummaryInfo';
import { GA_CATEGORY } from '~/src/features/tariffBuilder/constants/analytics';
import AgreementInfoContainer from '~/src/features/tariffBuilder/containers/AgreementInfoContainer';
import { useCtxSelector } from '~/src/features/tariffBuilder/hooks';
import getSummaryInfo from '~/src/features/tariffBuilder/selectors/getSummaryInfo';
import { selectFinalData } from '~/src/features/tariffBuilder/selectors/selectFinalData';
import { getAnalyticsSender } from '~/src/helpers/hooks/useAnaliticsSender';

import EmployeeVisitInfo from './EmployeeVisitInfo';

const Final = (): JSX.Element => {
    const sendAnalytics = getAnalyticsSender(GA_CATEGORY);
    const isLaptop = useMatchMediaByWidth(wideBreakpoints.laptop);
    const router = useRouter();

    const {
        agreementData,
        hasEquipment,
        slot,
    } = useCtxSelector(selectFinalData);

    const summaryInfo = useCtxSelector(getSummaryInfo);
    const { agreement } = useCtxSelector((state) => (state));

    const onGoToPayment = useCallback(() => {
        router.push({
            pathname:'/payments',
            query: {
                pay: 'online',
                agreement: agreement.agreementNumber,
                s: summaryInfo.total,
            },
        });

        sendAnalytics('click payment', 'btn: "Оплатить"');
    }, []);

    const dateTime = useMemo(() => {
        const [hour, minutes] = slot?.hour.split(':') || ['00', '00'];
        return `${slot?.date} c ${slot?.hour} по ${String(Number(hour) + 1).padStart(2, '0')}:${minutes}`;
    }, [slot?.date, slot?.hour]);

    return (
        <FullWidthListContainer $size={16} $direction={'column'}>
            {agreementData.isDouble &&
                <Alert Icon={InfoCircle} type={'error'}>
                    <Paragraph4>
                        К&nbsp;вашим данным уже привязан другой договор. Скоро позвоним, чтобы во&nbsp;всём разобраться.
                        Чтобы мы&nbsp;смогли дозвониться&nbsp;&mdash; проверьте, что у&nbsp;вас отключен автоответчик и&nbsp;антиспам приложения.
                    </Paragraph4>
                </Alert>
            }
            {agreementData.hasError && !agreementData.isDouble &&
                <Alert Icon={InfoCircle} type={'error'}>
                    <Paragraph4>
                        Не&nbsp;удалось создать договор. Скоро позвоним, чтобы во&nbsp;всём разобраться.
                        Чтобы мы&nbsp;смогли дозвониться&nbsp;&mdash; проверьте, что у&nbsp;вас отключен автоответчик и&nbsp;антиспам приложения.
                    </Paragraph4>
                </Alert>
            }
            {!agreementData.hasError &&
                <>
                    <Alert Icon={TickCircle} type={'success'}>
                        <Paragraph4>
                            Назначен визит инженера. В&nbsp;течение часа вам придет смс с&nbsp;информацией по&nbsp;вашей заявке.
                            Следите за&nbsp;статусом или изменяйте заявку в&nbsp;личном кабинете.
                        </Paragraph4>
                    </Alert>
                    <EmployeeVisitInfo
                        date={dateTime}
                        sla={'60 мин'}
                        isInstallationHardware={hasEquipment}
                    />
                    {isLaptop &&
                        <>
                            <AgreementInfoContainer/>
                            {!agreement.hasError &&
                                <H3>Оплатить можно только до&nbsp;или во&nbsp;время визита инженера</H3>
                            }
                            <BlockContainer
                                rounder={{
                                    $radius: 24,
                                }}
                                indent={{
                                    $united: [24],
                                }}
                            >
                                <FullWidthListContainer $direction={'column'} $size={24} $wrap='nowrap'>
                                    <SummaryInfo
                                        {...summaryInfo}
                                    />
                                    {!agreement.hasError &&
                                        <Button onClick={onGoToPayment}>Оплатить</Button>
                                    }
                                </FullWidthListContainer>
                            </BlockContainer>
                        </>
                    }
                </>
            }
            {!!dateTime && !agreementData.hasError &&
                <Link href={USER_LOGIN_ROUTE} passHref>
                    <Button
                        bType={'secondary'}
                    >
                        Перейти в личный кабинет
                    </Button>
                </Link>
            }
            <CallbackContacts/>
        </FullWidthListContainer>
    );
};

export default Final;
